import React, { createContext, useState, useContext, useCallback } from "react";
import { Animated, Easing, Platform } from "react-native";
import VDBIcon, { IconsVariant } from "@components/icons/VDBIcon";
import { VDBIcons } from "@utils/VDBIcons";
import { useTheme } from "styled-components/native";

interface Position {
  x: number;
  y: number;
}

interface AnimationContextProps {
  triggerAnimation: (startPos: Position) => void;
}

const AnimationContext = createContext<AnimationContextProps | undefined>(
  undefined
);

const FlyingHeart = ({
  startPosition,
  endPosition,
  onAnimationComplete,
}: {
  startPosition: Position;
  endPosition: Position;
  onAnimationComplete: () => void;
}) => {
  const theme = useTheme();
  const animation = React.useRef(new Animated.Value(0)).current;

  const translateX = endPosition.x - startPosition.x;
  const translateY = endPosition.y - startPosition.y;

  React.useEffect(() => {
    Animated.timing(animation, {
      toValue: 1,
      duration: 2000,
      easing: Easing.bezier(0.25, 0.1, 0.25, 1),
      useNativeDriver: Platform.OS !== "web",
    }).start(({ finished }) => {
      if (finished && onAnimationComplete) {
        onAnimationComplete();
      }
    });
  }, []);

  const currentTranslateX = animation.interpolate({
    inputRange: [0, 1],
    outputRange: [0, translateX],
  });

  const currentTranslateY = animation.interpolate({
    inputRange: [0, 1],
    outputRange: [0, translateY],
  });

  const scale = animation.interpolate({
    inputRange: [0, 0.3, 1],
    outputRange: [1, 1.2, 0.5],
  });

  const opacity = animation.interpolate({
    inputRange: [0, 0.8, 1],
    outputRange: [1, 1, 0],
  });

  return (
    <Animated.View
      style={[
        {
          position: "absolute",
          left: startPosition.x,
          top: startPosition.y,
          transform: [
            { translateX: currentTranslateX },
            { translateY: currentTranslateY },
            { scale },
          ],
          opacity,
          zIndex: 9999,
          pointerEvents: "none",
        },
      ]}
    >
      <VDBIcon
        color={theme["error-01"]}
        size={24}
        variant={IconsVariant.VDBCustom}
        name={VDBIcons.FAV}
      />
    </Animated.View>
  );
};

export const AnimationProvider = ({ children }) => {
  const [heartPropsList, setHeartPropsList] = useState<
    { id: string; startPosition: Position; endPosition: Position }[]
  >([]);

  const getFavoritesTabPosition = () => {
    if (Platform.OS !== "web" || typeof document === "undefined") {
      return { x: 0, y: 0 };
    }

    try {
      // We are having multiple favorites tabs in the header when coming from Global Navigation, so we need to find the one that is visible.
      const elements = document.querySelectorAll("#favorites-tab");
      let favTab = elements[0];
      if (elements.length > 1) {
        elements.forEach((el) => {
          const rect = el.getBoundingClientRect();
          if (rect.x > 0 && rect.y > 0) {
            favTab = el;
          }
        });
      }
      if (favTab) {
        const rect = favTab.getBoundingClientRect();
        return {
          x: rect.left + rect.width / 2,
          y: rect.top + rect.height / 2,
        };
      }
    } catch (e) {
      console.error("Error finding favorites tab:", e);
    }

    return { x: window.innerWidth - 100, y: 50 };
  };

  const triggerAnimation = useCallback((startPos: Position) => {
    if (Platform.OS !== "web") return;

    const endPos = getFavoritesTabPosition();
    const id = Math.random().toString(36).substr(2, 9);

    setHeartPropsList((prev) => [
      ...prev,
      {
        id,
        startPosition: startPos,
        endPosition: endPos,
      },
    ]);
  }, []);

  const removeHeart = (id: string) => {
    setHeartPropsList((prev) => prev.filter((h) => h.id !== id));
  };

  return (
    <AnimationContext.Provider value={{ triggerAnimation }}>
      {children}

      {Platform.OS === "web" && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            pointerEvents: "none",
            zIndex: 9999,
          }}
        >
          {heartPropsList.map((heart) => (
            <FlyingHeart
              key={heart.id}
              startPosition={heart.startPosition}
              endPosition={heart.endPosition}
              onAnimationComplete={() => removeHeart(heart.id)}
            />
          ))}
        </div>
      )}
    </AnimationContext.Provider>
  );
};

export const useAnimation = () => {
  const context = useContext(AnimationContext);
  if (context === undefined) {
    throw new Error("useAnimation must be used within an AnimationProvider");
  }
  return context;
};
